/** @jsxImportSource @emotion/react */

import { format } from 'date-fns';

import { AdminUser } from 'modules/organizers/domain/interfaces/organizer-details';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { baseApi } from 'shared/api/base-api';
import { AppDispatch } from 'store/store';
import { useDispatchWithToast } from 'store/use-dispatch-with-toast';
import Modal from 'ud-ui/components/modal';
import UDButton from 'ud-ui/components/button';
import UDText from 'ud-ui/components/text';
import { Plan } from 'modules/organizers/domain/interfaces/plan';
import { fetchActiveTournamentPlan, givePlan } from 'modules/organizers/store/actions';
import { selectActiveOrganizerPlans, selectPlansList } from 'modules/organizers/store/selectors';
import PlanCard from 'modules/organizers/ui/components/plan-card';
import { disableButtonCSS, MainBlock, PlansList } from './styles';
import { theme } from 'styles/theme/theme';

interface IProps {
  tournamentId: number;
  owner?: AdminUser;
}

function PlansBlock(props: IProps) {
  const dispatch = useDispatch<AppDispatch>();
  const dispatchWithToast = useDispatchWithToast();
  const { tournamentId, owner } = props;

  const plans = useSelector(selectPlansList);
  const [selectedPlan, setSelectedPlan] = useState<Plan>();
  const [tariff, setTariff] = useState<string>();
  // const { open, close, Modal } = useModal({ defaultShow: false });
  const activePlan = useSelector(selectActiveOrganizerPlans(tournamentId));
  const [currentPlanId, setCurrentPlanId] = useState<number>();
  const balance = useMemo(() => {
    return owner?.balance ? Number((owner?.balance / 100).toFixed(2)) : 0;
  }, [owner]);

  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (activePlan === undefined) {
      dispatchWithToast(fetchActiveTournamentPlan({ tournamentId: tournamentId }), {
        error: 'Не удалось получить активный план',
      });
    } else if (activePlan === null) {
      setCurrentPlanId(0);
    } else {
      setCurrentPlanId(activePlan.id);
    }
  }, [activePlan, dispatchWithToast, tournamentId]);

  const closeModal = useCallback(() => {
    setTariff(undefined);
    setSelectedPlan(undefined);
    close();
  }, [close]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [closeModal]);

  const handleGivePlan = useCallback(() => {
    if (owner === undefined) {
      toast.error('Пользователь не найден');
      return;
    }

    const planId = selectedPlan?.id;
    if (planId === undefined) {
      toast.error('Не удалось определить план');
      return;
    }

    dispatchWithToast(
      givePlan({
        tournamentId: tournamentId,
        recipientId: owner.id,
        tournamentPlanId: planId,
      }),
      { success: 'План подключен', error: 'Не удалось выдать план' },
    ).then((result) => {
      if (!result) return;

      dispatch(baseApi.util.invalidateTags([{ type: 'Organizer', id: tournamentId }]));
      dispatchWithToast(fetchActiveTournamentPlan({ tournamentId: tournamentId }), {
        error: 'Не удалось получить активный план',
      });
      closeModal();
    });
  }, [owner, selectedPlan?.id, dispatch, dispatchWithToast, tournamentId, closeModal]);

  const isCanBuy = Boolean(selectedPlan?.price && selectedPlan?.price <= balance);

  return (
    <MainBlock>
      <div className={'mb-7'}>
        <UDText type={'subhead'}>
          Данный пользователь использует план:{' '}
          <span style={{ color: theme.colors.AccentNaplesYellow, fontWeight: 'bold' }}>
            {plans.find((plan) => plan.id === currentPlanId)?.name}
          </span>
        </UDText>
      </div>
      <PlansList>
        {plans.map((plan, index) => {
          const planLength = plans.length;
          const isActive = plan.id === currentPlanId;
          const activeFrom = activePlan?.subscription?.activeFrom
            ? format(new Date(activePlan.subscription.activeFrom), 'dd.MM.yy')
            : undefined;
          const activeTo = activePlan?.subscription?.activeTo
            ? format(new Date(activePlan.subscription.activeTo), 'dd.MM.yy')
            : undefined;

          const onClick = () => {
            if (plan.id < 0) return;
            open();
            setSelectedPlan(plan);
          };

          return (
            <PlanCard
              isDisabled={plan.id < 0}
              onClick={onClick}
              key={plan.id}
              name={plan.name}
              price={plan.price}
              isActive={isActive}
              compact
              date={
                isActive
                  ? {
                      from: activeFrom,
                      to: activeTo,
                    }
                  : undefined
              }
              widthPercent={100 - (planLength - (index + 1)) * 5}
            />
          );
        })}
      </PlansList>

      <Modal
        show={isOpen}
        onClose={closeModal}
        // overlayProps={{
        //   id: 'overlay',
        //   onClick: (event) => {
        //     event.preventDefault();
        //     const target = event.target as HTMLElement;
        //     if (target?.id === 'overlay') {
        //       closeModal();
        //     }
        //   },
        // }}
        // containerProps={{ css: modalContainerCss }}
        // closeIconProps={{ style: { paddingInline: 22, paddingBlock: 22 }, className: 'close' }}
      >
        {selectedPlan && selectedPlan?.id === activePlan?.id ? (
          <div className={'flex flex-col items-start'}>
            <UDText type={'title-3'} style={{ marginBottom: 30 }}>
              {selectedPlan.name} план
            </UDText>
            <UDText type={'subhead'} style={{ marginBottom: 26 }}>
              План подключен и будет действовать до{' '}
              <span style={{ fontWeight: 'bold' }}>
                {activePlan?.subscription?.activeTo ? format(new Date(activePlan.subscription.activeTo), 'dd.MM.yy') : "'Неизвестно'"}
              </span>
            </UDText>
            <UDButton style={{ width: 130 }} onClick={closeModal} variant={'primary'}>
              Понятно
            </UDButton>
          </div>
        ) : (
          <div className={'flex flex-col items-start'}>
            <UDText type={'title-3'} style={{ marginBottom: 30 }}>
              {selectedPlan?.name} план
            </UDText>
            <UDText type={'subhead'} style={{ marginBottom: 16 }}>
              Подключить этот план:
            </UDText>
            <div style={{ marginBottom: 30, display: 'flex' }}>
              <UDButton
                variant="secondary"
                onClick={() => setTariff('month')}
                className={'flex-center'}
                style={{
                  marginRight: 16,
                  border: tariff === 'month' ? `2px solid ${theme.colors.AccentNaplesYellow}` : '2px solid transparent',
                }}
              >
                На 1 месяц
              </UDButton>
              <UDButton
                variant="secondary"
                disabled
                onClick={() => setTariff('year')}
                className={'flex-center'}
                style={{
                  border: tariff === 'year' ? `2px solid ${theme.colors.AccentNaplesYellow}` : '2px solid transparent',
                  cursor: 'not-allowed',
                }}
              >
                На 12 месяцев
              </UDButton>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
              <UDButton
                css={disableButtonCSS}
                disabled={!tariff || !isCanBuy}
                style={{ width: 186, height: 36 }}
                onClick={handleGivePlan}
                variant={'primary'}
              >
                Подключить
              </UDButton>
              <div className="flex flex-col" style={{ gap: 4 }}>
                <UDText type={'caption-1'} style={{ color: theme.colors.DisabledInputColor }}>
                  один месяц за {selectedPlan?.price} руб.
                </UDText>
                {!isCanBuy && (
                  <UDText type={'caption-1'} style={{ color: theme.colors.DisabledInputColor }}>
                    недостаточно средств: {(selectedPlan?.price ?? 0) - balance} руб.
                  </UDText>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </MainBlock>
  );
}

export default PlansBlock;
